import {
  Archive,
  Briefcase,
  Compass,
  FileText,
  Folders,
  Gear,
  House,
  IdentificationCard,
  MagnifyingGlass,
  PencilSimple,
  Storefront,
  Trash,
} from "@phosphor-icons/react";
import { createAction } from "~/actions/registry";
import type { Action } from "~/actions/types";
import { ABILITY } from "~/api/types";
import { settingsSections } from "~/features/settings/registry";
import { CURRENT_WORKSPACE } from "~/policies/usePolicy";

const section = (ctx: { t: (key: string) => string }) => ctx.t("Navigation");

function navigateAction(
  id: string,
  label: string,
  to: string,
  icon: typeof House,
  keywords: string,
) {
  return createAction({
    id: `navigate.${id}`,
    name: (ctx) => ctx.t(label),
    section,
    icon,
    keywords,
    perform: (ctx) => ctx.navigate(to),
  });
}

export const navigateHome = navigateAction("home", "Home", "/", House, "start dashboard overview");
export const navigateSearch = navigateAction(
  "search",
  "Search",
  "/search",
  MagnifyingGlass,
  "find lookup",
);
export const navigateDiscover = navigateAction(
  "discover",
  "Discover",
  "/discover",
  Compass,
  "explore popular trending",
);
export const navigateCollections = navigateAction(
  "collections",
  "Collections",
  "/collections",
  Folders,
  "folders spaces",
);
export const navigateDocuments = navigateAction(
  "documents",
  "Documents",
  "/documents",
  FileText,
  "pages docs list",
);
export const navigateDrafts = navigateAction(
  "drafts",
  "Drafts",
  "/drafts",
  PencilSimple,
  "unpublished wip",
);
export const navigateArchive = navigateAction(
  "archive",
  "Archive",
  "/archive",
  Archive,
  "archived old",
);
export const navigateTrash = navigateAction(
  "trash",
  "Trash",
  "/trash",
  Trash,
  "deleted bin recycle",
);
export const navigateMarket = navigateAction(
  "market",
  "Market",
  "/market",
  Storefront,
  "marketplace hire services projects freelance",
);
export const navigateMyMarket = navigateAction(
  "my-listings",
  "My listings",
  "/market/me",
  Briefcase,
  "marketplace listings inquiries promote",
);
export const navigateMarketProfile = navigateAction(
  "market-profile",
  "Profile CV",
  "/market/profile",
  IdentificationCard,
  "cv resume skills portfolio availability",
);

export const navigateSettings = createAction({
  id: "navigate.settings",
  name: (ctx) => ctx.t("Settings"),
  section,
  icon: Gear,
  keywords: "preferences admin workspace",
  // Server-driven: the workspace says whether this viewer may administer it.
  visible: (ctx) => ctx.abilities(CURRENT_WORKSPACE)[ABILITY.workspaceUpdate] === true,
  perform: (ctx) => ctx.navigate("/settings"),
});

/**
 * Every settings section is reachable from the command bar without being declared twice:
 * the registry is the single source, and these are generated from it.
 */
export const settingsJumpActions: Action[] = settingsSections.map((entry) =>
  createAction({
    id: `settings.${entry.id}`,
    name: (ctx) => ctx.t(entry.title),
    section: (ctx) => ctx.t("Settings"),
    icon: entry.icon,
    ...(entry.keywords ? { keywords: `settings ${entry.keywords}` } : { keywords: "settings" }),
    visible: (ctx) => (entry.visible ? entry.visible(ctx.abilities(CURRENT_WORKSPACE)) : true),
    perform: (ctx) => ctx.navigate(`/settings/${entry.id}`),
  }),
);

export const navigationActions: Action[] = [
  navigateHome,
  navigateSearch,
  navigateDiscover,
  navigateCollections,
  navigateDocuments,
  navigateDrafts,
  navigateArchive,
  navigateTrash,
  navigateMarket,
  navigateMyMarket,
  navigateMarketProfile,
  navigateSettings,
];
